"use client";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { Icons } from "@/components/icons";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const radioButtonVariants = cva("", {
	variants: {
		variant: {
			default:
				"ring-offset-background focus-visible:ring-ring aspect-square size-4 rounded-full border border-primary text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
			"checkbox-style":
				"w-full flex items-center justify-between gap-x-2 rounded-md border border-examine-purple-100 p-2 text-sm font-medium text-primary transition-colors duration-200 hover:bg-primary hover:text-white data-[state=checked]:bg-primary data-[state=checked]:text-white",
		},
		size: { sm: "", md: "" },
	},
	compoundVariants: [
		{ variant: "checkbox-style", size: "sm", class: "p-2" },
		{ variant: "checkbox-style", size: "md", class: "px-5 py-4" },
	],
	defaultVariants: {
		variant: "default",
		size: "sm",
	},
});

const RadioGroup = ({
	className,
	...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) => {
	return <RadioGroupPrimitive.Root className={cn("grid", className)} {...props} />;
};
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

type RadioGroupItemProps = React.ComponentProps<typeof RadioGroupPrimitive.Item> &
	VariantProps<typeof radioButtonVariants> & {
		label?: string;
		containerClassName?: string;
	};

const RadioGroupItem = ({
	className,
	containerClassName,
	children,
	label,
	variant = "default",
	size,
	...props
}: RadioGroupItemProps) => {
	return (
		<div className={cn("flex flex-1 items-center gap-2", containerClassName)}>
			<RadioGroupPrimitive.Item
				className={cn(radioButtonVariants({ variant, size }), className)}
				{...props}
			>
				{variant === "checkbox-style" && (
					<Label htmlFor={props.id} className="whitespace-nowrap text-sm lg:text-sm">
						{label}
					</Label>
				)}
				<RadioGroupPrimitive.Indicator className="flex items-center justify-center">
					{variant === "checkbox-style" ? (
						<Icons.CircleCheck className="size-5 fill-white text-primary" />
					) : (
						<Icons.Circle className="size-2.5 fill-current text-current" />
					)}
				</RadioGroupPrimitive.Indicator>
			</RadioGroupPrimitive.Item>

			{variant === "default" && label?.length && (
				<Label htmlFor={props.id} className="cursor-pointer text-base font-normal leading-normal">
					{label}
				</Label>
			)}
		</div>
	);
};
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

const RadioGroupIndicator = ({
	className,
	...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Indicator>) => {
	return (
		<RadioGroupPrimitive.Indicator className={cn("ring-offset-background", className)} {...props} />
	);
};
RadioGroupIndicator.displayName = RadioGroupPrimitive.Indicator.displayName;

export { RadioGroup, RadioGroupItem, RadioGroupIndicator };
